import React from 'react';

import { useDataAttributes } from '@lumapps/data-attributes';
import { useHover } from '@lumapps/lumx-hooks/hooks/useHover';
import { mdiBellRing, mdiBellOff } from '@lumapps/lumx/icons';
import { ButtonProps, Emphasis, IconButton } from '@lumapps/lumx/react';
import { useTranslate } from '@lumapps/translations';

import { SUBSCRIPTIONS } from '../../keys';

const SCOPE = 'notification';

export interface NotificationButton extends ButtonProps {
    /** Whether it's in loading state or not */
    isLoading?: boolean;
}

export const NotificationButton: React.FC<NotificationButton> = ({ isSelected, isLoading, scope, style, ...props }) => {
    const { get } = useDataAttributes(scope || SCOPE);
    const { translateKey } = useTranslate();

    const { isHovered, registerElement } = useHover();

    const buttonLabel = translateKey(
        isSelected
            ? SUBSCRIPTIONS.UNSUBSCRIBE_NOTIFICATIONS_HELP_TEXT
            : SUBSCRIPTIONS.SUBSCRIBE_NOTIFICATIONS_HELP_TEXT,
    );

    const icon = React.useMemo(() => {
        // Invert icon on hover or while loading
        const shouldInvertIcon = isHovered || isLoading;
        if (isSelected) {
            return shouldInvertIcon ? mdiBellOff : mdiBellRing;
        }
        return shouldInvertIcon ? mdiBellRing : mdiBellOff;
    }, [isHovered, isLoading, isSelected]);

    return (
        <IconButton
            label={buttonLabel}
            isSelected={isSelected}
            ref={registerElement}
            emphasis={Emphasis.medium}
            icon={icon}
            // Keep pointer events to allow blur while loading (and disabled)
            style={{ pointerEvents: 'initial', ...style }}
            {...get({ element: 'button', action: isSelected ? 'disable-notification' : 'enable-notification' })}
            {...props}
            disabled={isLoading || props.disabled}
        />
    );
};
